import type { FC } from 'react';
import { useState, useEffect } from 'react';
import {
  IconContainer,
  TrainingSecondaryButton,
  Title,
  TrainingCard,
  ProgressBar,
  ProgressFill,
  ProgressBarContainer,
  ProgressText,
} from '../TrainingCard';
import Play from '../../../../assets/Play.svg';
import { NamedContentEntryText } from '../../../ContentEntryText/NamedContentEntryText';
import { useStartVoluntaryTrainingQuery } from '../../../../apiHooks/useStartVoluntaryTrainingQuery';
import { useStartVoluntaryTrainingMutation } from '../../../../apiHooks/useStartVoluntaryTrainingMutation';
import { Spinner } from '../../../Spinner/Spinner';
import type {
  EnrolledCampaignListResponse,
  VoluntaryItem,
  VoluntaryTrainingListResponse,
} from '../../../../apiHooks/useCampaignsQuery';
import { Icon, IconType } from '../../../Props/Icon/Icon';
import { PlusCircledIcon } from '@radix-ui/react-icons';
import { Coin } from '../../../Props';
import { Tooltip } from '../../../Tooltip';
import styled, { useTheme } from 'styled-components';

type VoluntaryTrainingCardProps = {
  campaign: VoluntaryItem;
  onComplete: () => void;
};

export const VoluntaryTrainingCard: FC<VoluntaryTrainingCardProps> = ({
  campaign,
  onComplete,
}: VoluntaryTrainingCardProps) => {
  const { id, name } = campaign;
  const isLoading = (campaign as VoluntaryTrainingListResponse).isLoading;
  const isActive = (campaign as VoluntaryTrainingListResponse).isLoading === undefined;
  const [shouldFetchTrainingStatus, setShouldFetchTrainingStatus] = useState(false);
  const [isTrainingPending, setIsTrainingPending] = useState(isLoading);
  const { modulesProgress, modulesTotal } = campaign as EnrolledCampaignListResponse;
  const progress = modulesTotal > 0 ? (modulesProgress / modulesTotal) * 100 : 0;

  const { data: trainingData } = useStartVoluntaryTrainingQuery({
    id,
    options: {
      enabled: shouldFetchTrainingStatus,
      refetchInterval: shouldFetchTrainingStatus ? 5000 : false,
    },
  });

  const { mutate } = useStartVoluntaryTrainingMutation();

  useEffect(() => {
    if (trainingData?.isLoading === false) {
      setShouldFetchTrainingStatus(false);
      setIsTrainingPending(false);
      onComplete();
    } else if (trainingData?.isLoading === true) {
      setIsTrainingPending(true);
    }
  }, [trainingData, onComplete]);

  useEffect(() => {
    if (isLoading) {
      setShouldFetchTrainingStatus(true);
      setIsTrainingPending(true);
    }
  }, [isLoading]);

  const handleStartTraining = () => {
    if ((campaign as VoluntaryTrainingListResponse).isLoading === undefined) {
      if ((campaign as EnrolledCampaignListResponse).campaignUrl) {
        window.open((campaign as EnrolledCampaignListResponse).campaignUrl, '_blank', 'noopener,noreferrer');
      }
    } else {
      mutate(
        { campaignId: id },
        {
          onSuccess: () => {
            setShouldFetchTrainingStatus(true);
            setIsTrainingPending(true);
          },
        },
      );
    }
  };

  const theme = useTheme();

  return (
    <TrainingCard $wrap={isActive}>
      <IconContainer>
        {isActive ? (
          <Icon icon={IconType.GOAL_FLAG} width={20} height={20} fill="transparent" />
        ) : (
          <Play className="rtl-flip" />
        )}
      </IconContainer>
      <Title>{name}</Title>
      {!isActive && <Coin value={(campaign as VoluntaryTrainingListResponse).points} />}
      {isActive ? (
        <TrainingSecondaryButton onClick={handleStartTraining} disabled={isTrainingPending} $isLoading={isLoading}>
          <NamedContentEntryText container="span" refKey="yourTraining" subKey="startNow" />
        </TrainingSecondaryButton>
      ) : isTrainingPending ? (
        <Spinner size={'10px'} color={'blue'} />
      ) : (
        <Tooltip
          placement="left"
          content={<AddVoluntaryTrainingTooltip>Add to voluntary training</AddVoluntaryTrainingTooltip>}
        >
          <PlusCircledIcon onClick={handleStartTraining} className="plus-circled-icon" width={20} height={20} />
        </Tooltip>
      )}
      {isActive && (
        <ProgressContainer>
          <ProgressBarContainer>
            <ProgressBar>
              <ProgressFill progress={progress} backgroundColor={theme.colors.prototype.primary} />
            </ProgressBar>
            <ProgressText>
              {modulesProgress} / {modulesTotal}
            </ProgressText>
          </ProgressBarContainer>
        </ProgressContainer>
      )}
    </TrainingCard>
  );
};

const AddVoluntaryTrainingTooltip = styled.div`
  width: max-content;
  text-align: center;
  padding: 8px;
  border-radius: 4px;
`;

const ProgressContainer = styled.div`
  width: 100%;
  flex-grow: 1;
  margin-top: 8px;
`;
